import {
  APICountInstancesResponse,
  DirectedTripleWithFilters,
  LinkedComponent,
} from '@ardoq/api-types';
import { partialPayload, pipeReducers } from '@ardoq/common-helpers';
import { anyType } from '@ardoq/global-consts';
import { pathOperations } from 'traversals/pathOperations';
import { isDefaultWildcardTraversal } from 'traverseOneLevel/defaultWildcardViewpoint';
import { getId } from '../getId';
import { RawGraphEdges, TraversalBuilderState, TripleOptions } from '../types';
import { createOptions } from './createOptions';
import { createStartNode } from './createStartNode';
import {
  ChangeWildcardTraversalPayload,
  SetTraversalsPayload,
} from './editTraversalActions';
import {
  getHasTripleOptions,
  tripleOptionToFilteredAndSortedTripleOptions,
} from './filterOptions';
import { getEmptySelectedNodeData } from './getEmptySelectedNodeData';
import { getReferenceIdsBetweenStartAndSelectedContext } from './getReferenceIdsBetweenStartAndSelectedContext';
import {
  getStartSetFilterId,
  getTraversals,
  isNonWildcardTriple,
  isTripleWithSourceOrTargetType,
  toDirectedTriples,
} from './getTraversals';
import { setOptionsInstanceCounts } from './instanceCounts';
import { pathsToGraph } from './pathsToGraph';
import { updateFilters } from './updateFilters';
import { updateSelectedGraph } from './updateSelectedGraph';
import { viewpointBuilderFiltersOperations } from './viewpointBuilderFiltersOperations';

const getWildcardReferenceIds = (rawGraphEdges: RawGraphEdges) => {
  const references = Array.from(rawGraphEdges.values());
  return new Set(
    references
      .filter(({ label }) => label === anyType)
      .map(({ modelId }) => modelId)
  );
};

const getTriplesOptions = (
  state: TraversalBuilderState,
  { isFetchCounts = true } = {}
): TripleOptions => {
  const {
    selectedGraphNodeId,
    selectedGraphEdgeId,
    rawGraphEdges,
    baseGraph,
    graphNodes,
    graphEdges,
    tripleOptions: { showOnlyOptionsWithInstanceCounts },
  } = state;
  if (selectedGraphEdgeId) {
    const edgeModelId = graphEdges.get(selectedGraphEdgeId)!.modelId;
    return {
      outgoing: [],
      incoming: [],
      instanceCountsResponse: null,
      selectedNode: getEmptySelectedNodeData(),
      selectedEdge: {
        metaDataComponent: rawGraphEdges.get(edgeModelId)!.metaData,
      },
      fetchCountsArgs: null,
      optionCountLoadingState: 'idle',
      showOnlyOptionsWithInstanceCounts,
    };
  }

  if (!selectedGraphNodeId)
    return {
      outgoing: [],
      incoming: [],
      instanceCountsResponse: null,
      selectedNode: getEmptySelectedNodeData(),
      fetchCountsArgs: null,
      optionCountLoadingState: 'idle',
      showOnlyOptionsWithInstanceCounts,
    };
  const graphNode = graphNodes.get(selectedGraphNodeId)!;
  const id = graphNode.modelId;
  const wildcardReferences = getWildcardReferenceIds(state.rawGraphEdges);
  const isNotWildcardReference = ({ referenceId }: LinkedComponent) =>
    !wildcardReferences.has(referenceId);
  const outgoingEdges = (baseGraph.sourceMap.get(id) ?? [])
    .filter(isNotWildcardReference)
    .map(({ referenceId, componentId }) => ({
      sourceId: id,
      targetId: componentId,
      referenceId,
    }));
  const incomingEdges = (baseGraph.targetMap.get(id) ?? [])
    .filter(isNotWildcardReference)
    .map(({ referenceId, componentId }) => ({
      targetId: id,
      sourceId: componentId,
      referenceId,
    }));

  const referenceIdsToStartContext = new Set(
    getReferenceIdsBetweenStartAndSelectedContext({
      ...state.selectedGraph,
      currentContextId: state.startNode!.id,
      targetContextId: selectedGraphNodeId,
    })
  );

  const triplePathForSelectedNode = getTraversals(state, selectedGraphNodeId);
  const filters =
    viewpointBuilderFiltersOperations.mapFiltersToBackendFormat(state);

  return createOptions({
    state,
    graphNode,
    outgoingEdges,
    incomingEdges,
    triplePathForSelectedNode,
    filters,
    shouldFetchCounts: isFetchCounts,
    startSetFilterId: getStartSetFilterId(state),
    referenceIdsToStartContext,
  });
};

export const setTriplesOptions = (
  state: TraversalBuilderState,
  instanceCountsResponse: APICountInstancesResponse | null = null
): TraversalBuilderState => {
  const tripleOptions = getTriplesOptions(state, {
    isFetchCounts: !instanceCountsResponse,
  });

  const stateWithTripleOptionsWithInstanceCounts = setOptionsInstanceCounts(
    {
      ...state,
      tripleOptions,
    },
    instanceCountsResponse
  );

  const filteredAndSortedTripleOptions =
    tripleOptionToFilteredAndSortedTripleOptions(
      stateWithTripleOptionsWithInstanceCounts.tripleOptions,
      state.triplesSortAndFiltersState,
      state.locale
    );

  return {
    ...stateWithTripleOptionsWithInstanceCounts,
    hasTripleOptions: getHasTripleOptions(
      tripleOptions,
      state.triplesSortAndFiltersState
    ),
    filteredTripleOptions: filteredAndSortedTripleOptions,
  };
};

const toWildcardState = (
  state: TraversalBuilderState,
  { paths }: SetTraversalsPayload
): TraversalBuilderState => {
  if (!isDefaultWildcardTraversal(paths)) {
    return { ...state, wildcardState: null };
  }

  const startTypeFromPath = pathOperations.getStartTypeFromPaths(paths);

  if (startTypeFromPath === state.startComponentType) {
    return {
      ...state,
      wildcardState: {
        startComponentType: state.startComponentType!,
        startNode: state.startNode!,
      },
    };
  }

  const { startNode } = createStartNode({
    startType: startTypeFromPath,
    traversalStartSet: [],
    traversalStartQueryResultCount: undefined,
    rawGraphNodes: state.rawGraphNodes,
    isHierarchyWildcardActive: Boolean(state.startSetExpansionState),
  });

  if (!startNode) {
    return { ...state, wildcardState: null };
  }

  return {
    ...state,
    startComponentType: startTypeFromPath,
    startNode,
    wildcardState: {
      startComponentType: state.startComponentType!,
      startNode: state.startNode!,
    },
  };
};

/**
 * The references the hierarchy node stands for: `applyStartSetExpansion` collects
 * one per (reference type, far type) from the hierarchy's members and keys them
 * under `anyType`.
 */
const getHierarchyNodeReferenceIds = (
  state: TraversalBuilderState
): ReadonlySet<string> =>
  new Set(
    [
      ...(state.baseGraph.sourceMap.get(anyType) ?? []),
      ...(state.baseGraph.targetMap.get(anyType) ?? []),
    ].map(({ referenceId }) => referenceId)
  );

const setTraversalInternal = (
  state: TraversalBuilderState,
  { paths, filters }: SetTraversalsPayload,
  getId_: () => string = getId
): TraversalBuilderState => {
  if (state.rawGraphNodes.size === 0 || !state.startComponentType) {
    return {
      ...state,
      initialPaths: paths ?? null,
      initialFilters: filters ?? null,
    };
  }

  const {
    graphEdges,
    graphNodes,
    referenceMap,
    startGraphNode: startNode,
    instanceCounts,
    filterIdToGraphNodeIdMap,
    requiredNodeIds,
  } = pathsToGraph({
    paths,
    filters,
    startType: state.startComponentType,
    startNode: state.startNode,
    getId: getId_,
    rawGraphEdges: state.rawGraphEdges,
    rawGraphNodes: state.rawGraphNodes,
    instanceCounts: state.instanceCounts,
    wildcardReferenceIds: getHierarchyNodeReferenceIds(state),
  });

  const selectedGraph = updateSelectedGraph(referenceMap);

  const selectedGraphNodeId =
    state.selectedGraphNodeId && graphNodes.has(state.selectedGraphNodeId)
      ? state.selectedGraphNodeId
      : startNode.id;

  if (selectedGraphNodeId) {
    const selectedGraphNode = graphNodes.get(selectedGraphNodeId);
    if (selectedGraphNode) {
      selectedGraphNode.setIsSelected(true);
    }
  }
  return viewpointBuilderFiltersOperations.viewpointFiltersToTraversalBuilderFilters(
    setTriplesOptions({
      ...state,
      initialPaths: paths ?? null,
      initialFilters: filters ?? null,
      startNode,
      selectedGraphNodeId,
      graphEdges,
      graphNodes,
      selectedGraph,
      instanceCounts,
      requiredNodeIds,
    }),
    filters,
    filterIdToGraphNodeIdMap
  );
};

// Impure version, setTraversalInternal uses the side effecting getId function
// as default argument.
export const setTraversal = (
  state: TraversalBuilderState,
  payload: SetTraversalsPayload
): TraversalBuilderState =>
  setTraversalInternal(toWildcardState(state, payload), payload);

export const updateTripleOptions = (state: TraversalBuilderState) =>
  pipeReducers(
    state,
    partialPayload(
      setTriplesOptions,
      state.tripleOptions.instanceCountsResponse
    ),
    updateFilters
  );

export const expandDefaultTraversal = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  if (!state.wildcardState) {
    return state;
  }

  const { startComponentType, startNode } = state.wildcardState;
  const paths: DirectedTripleWithFilters[][] = state.metaModelTriples
    .filter(isTripleWithSourceOrTargetType(startComponentType))
    .filter(isNonWildcardTriple)
    .flatMap(toDirectedTriples(startComponentType));

  return setTraversal(
    {
      ...state,
      startNode,
      startComponentType: startComponentType,
      wildcardState: null,
    },
    { paths, filters: null }
  );
};

export const changeWildcardTraversal = (
  state: TraversalBuilderState,
  { paths }: ChangeWildcardTraversalPayload
): TraversalBuilderState => setTraversal(state, { paths, filters: null });
